/**
 * Consimtamantul de urmarire: starea, bannerul si legatura cu scripturile.
 *
 * Bannerul apare **tuturor vizitatorilor**, nu doar celor din UE. Varianta
 * „doar UE" exista in POS si e gresita in doua feluri deodata: juridic (nu
 * cetatenia decide, ci unde e stabilit operatorul — o firma din Romania are
 * aceleasi obligatii fata de un vizitator din afara UE) si tehnic (acolo,
 * vizitatorii din afara UE ramaneau pe refuz implicit si nu vedeau niciodata
 * bannerul, deci pixelul nu pornea niciodata pentru ei — exact invers decat
 * intentia autorului).
 */

import {
  CONSENT_TEXTS_RO,
  CONSENT_TEXT_VERSION_DEFAULT,
  type ConsentDecision,
  type ConsentState,
} from "@deliveryapp/shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { recordConsent } from "../api/client";
import { useBootstrap } from "../components/BootstrapProvider";
import { Button } from "../components/ui";
import { clearConsent, readAnonymousId, readConsent, writeConsent } from "./storage";
import { applyTracking, trackingIsLive } from "./tracking";

interface ConsentContextValue {
  state: ConsentState;
  /** Textele in vigoare, deja rezolvate din config. */
  textVersion: string;
  openPreferences: () => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function useConsent(): ConsentContextValue {
  const value = useContext(ConsentContext);
  if (value === null) {
    throw new Error("useConsent a fost apelat in afara lui ConsentProvider.");
  }
  return value;
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const { marketKey, config, t } = useBootstrap();

  const textVersion = config.consent?.textVersion ?? CONSENT_TEXT_VERSION_DEFAULT;
  const bannerBody =
    config.consent?.bannerBody ?? t("consent.banner_body", CONSENT_TEXTS_RO.bannerBody);

  const [state, setState] = useState<ConsentState>(() => readConsent(marketKey, textVersion));
  const [panelOpen, setPanelOpen] = useState(false);

  // Decizia stocata se aplica la fiecare montare: scripturile traiesc in pagina,
  // nu in localStorage, deci o reincarcare trebuie sa le repuna.
  useEffect(() => {
    if (state === null) return;
    applyTracking(config, { analytics: state.analytics, marketing: state.marketing });
  }, [config, state]);

  const persist = useCallback(
    (decision: ConsentDecision) => {
      writeConsent(marketKey, decision);
      setState(decision);
      setPanelOpen(false);

      // Aplicam INAINTE de a trimite: decizia nu depinde de server.
      applyTracking(config, { analytics: decision.analytics, marketing: decision.marketing });

      const snapshot = `${CONSENT_TEXTS_RO.bannerTitle}\n\n${bannerBody}`;
      void recordConsent(marketKey, {
        anonymousId: readAnonymousId(marketKey),
        textVersion: decision.textVersion,
        decisions: [
          { purpose: "analytics", granted: decision.analytics, textSnapshot: snapshot },
          { purpose: "marketing", granted: decision.marketing, textSnapshot: snapshot },
        ],
        // Jurnalul e pentru dovada, nu pentru efect. Daca cererea pica, decizia
        // clientului ramane respectata; ce se pierde e o linie de evidenta, si
        // nu are ce mesaj de eroare sa primeasca omul pentru asta.
      }).catch(() => undefined);
    },
    [bannerBody, config, marketKey],
  );

  const decide = useCallback(
    (analytics: boolean, marketing: boolean) => {
      const previous = state;
      const decision: ConsentDecision = {
        analytics,
        marketing,
        textVersion,
        decidedAt: new Date().toISOString(),
      };
      persist(decision);

      // Retragerea unui scop deja pornit: scriptul e in pagina si nu se poate
      // scoate. Reincarcarea e singura stare in care refuzul e chiar respectat —
      // a pretinde altceva ar fi comod si fals.
      const revoked =
        previous !== null &&
        ((previous.analytics && !analytics) || (previous.marketing && !marketing));
      if (revoked && trackingIsLive()) {
        window.location.reload();
      }
    },
    [persist, state, textVersion],
  );

  const openPreferences = useCallback(() => setPanelOpen(true), []);

  const value = useMemo<ConsentContextValue>(
    () => ({ state, textVersion, openPreferences }),
    [state, textVersion, openPreferences],
  );

  const needsDecision = state === null || panelOpen;

  return (
    <ConsentContext.Provider value={value}>
      {children}
      {needsDecision ? (
        <ConsentBanner
          body={bannerBody}
          initial={state}
          dismissable={panelOpen && state !== null}
          onDismiss={() => setPanelOpen(false)}
          onDecide={decide}
          t={t}
        />
      ) : null}
    </ConsentContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Bannerul
// ---------------------------------------------------------------------------

function ConsentBanner({
  body,
  initial,
  dismissable,
  onDismiss,
  onDecide,
  t,
}: {
  body: string;
  initial: ConsentState;
  dismissable: boolean;
  onDismiss: () => void;
  onDecide: (analytics: boolean, marketing: boolean) => void;
  t: (key: string, fallback: string) => string;
}) {
  // Niciuna nu e bifata din start cand nu exista decizie. O caseta prebifata nu
  // e consimtamant — e o optiune pe care omul trebuie sa o observe si sa o
  // dezactiveze, adica exact ce interzice regula.
  const [analytics, setAnalytics] = useState(initial?.analytics ?? false);
  const [marketing, setMarketing] = useState(initial?.marketing ?? false);
  const [detailed, setDetailed] = useState(initial !== null);

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="consent-title"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-canvas p-3 shadow-lg sm:p-5"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h2 id="consent-title" className="text-sm font-bold text-ink sm:text-base">
            {t("consent.banner_title", CONSENT_TEXTS_RO.bannerTitle)}
          </h2>
          {/* Compact pe telefon: bannerul acopera meniul, deci textul lui trebuie
              sa se citeasca dintr-o privire. Nu e ascuns nimic — textul e intreg. */}
          <p className="text-xs leading-relaxed text-ink-muted sm:text-sm">{body}</p>
        </div>

        {detailed ? (
          <div className="flex flex-col gap-3 rounded-card border border-line p-3">
            <ConsentToggle
              checked={analytics}
              onChange={setAnalytics}
              label={t("consent.analytics_label", CONSENT_TEXTS_RO.analyticsLabel)}
              hint={t("consent.analytics_hint", CONSENT_TEXTS_RO.analyticsHint)}
            />
            <ConsentToggle
              checked={marketing}
              onChange={setMarketing}
              label={t("consent.marketing_label", CONSENT_TEXTS_RO.marketingLabel)}
              hint={t("consent.marketing_hint", CONSENT_TEXTS_RO.marketingHint)}
            />
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {detailed ? (
            <Button variant="primary" onClick={() => onDecide(analytics, marketing)}>
              {t("consent.save", CONSENT_TEXTS_RO.save)}
            </Button>
          ) : (
            <>
              {/* «Doar cele necesare» sta primul si are ACEEASI greutate vizuala
                  (aceeasi marime, aceeasi latime) ca acceptarea. Un refuz ascuns
                  sub un link gri, langa un buton colorat de accept, e un
                  consimtamant obtinut prin design. „Setari" e actiunea a treia,
                  mai mica, fiindca nu e o decizie, e o cale spre una. */}
              <Button variant="secondary" className="flex-1 sm:flex-none" onClick={() => onDecide(false, false)}>
                {t("consent.reject_all", CONSENT_TEXTS_RO.rejectAll)}
              </Button>
              <Button variant="primary" className="flex-1 sm:flex-none" onClick={() => onDecide(true, true)}>
                {t("consent.accept_all", CONSENT_TEXTS_RO.acceptAll)}
              </Button>
              <Button variant="ghost" size="sm" className="w-full sm:w-auto" onClick={() => setDetailed(true)}>
                {t("consent.manage", CONSENT_TEXTS_RO.manage)}
              </Button>
            </>
          )}
          {dismissable ? (
            <Button variant="ghost" size="sm" onClick={onDismiss}>
              Renunță
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ConsentToggle({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <label className="flex items-start gap-3 text-sm text-ink">
      <input
        type="checkbox"
        className="mt-1"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="flex flex-col gap-0.5">
        <span className="font-medium">{label}</span>
        <span className="text-ink-soft">{hint}</span>
      </span>
    </label>
  );
}

/** Buton de subsol prin care decizia se poate schimba oricand. */
export function ConsentPreferencesLink({ className = "" }: { className?: string }) {
  const { openPreferences } = useConsent();
  return (
    <button type="button" onClick={openPreferences} className={className}>
      {CONSENT_TEXTS_RO.bannerTitle}
    </button>
  );
}

/** Curata decizia. Folosita din pagina de reinitializare a preferintelor. */
export function forgetConsent(marketKey: string): void {
  clearConsent(marketKey);
}
